document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-ts-scroll]').forEach((button) => {
    button.addEventListener('click', () => document.querySelector(button.dataset.tsScroll)?.scrollIntoView({ behavior: 'smooth' }));
  });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const compactMobile = window.matchMedia('(max-width: 749px)').matches;
  const rb22Videos = document.querySelectorAll('[data-ts-rb22-video]');

  rb22Videos.forEach((video) => {
    const poster = compactMobile ? video.dataset.mobilePoster : video.dataset.poster;
    if (poster) video.poster = poster;
  });

  document.querySelectorAll('[data-ts-video-toggle]').forEach((button) => {
    const video = button.closest('.ts-rb22__video-shell')?.querySelector('[data-ts-rb22-video]');
    if (!video) return;
    const syncLabel = () => {
      button.textContent = video.paused ? 'PLAY' : 'PAUSE';
      button.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} RB22 video`);
    };
    button.addEventListener('click', () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
      window.setTimeout(syncLabel, 0);
    });
    video.addEventListener('play', syncLabel);
    video.addEventListener('pause', syncLabel);
    syncLabel();
  });

  // Keep the large launch film opt-in. Loading it only after a visitor presses
  // play avoids spending bandwidth on a 13 MB desktop asset during browsing.

  const lifestyle = document.querySelector('[data-ts-lifestyle]');
  if (lifestyle) {
    const visual = lifestyle.querySelector('[data-ts-lifestyle-visual]');
    const buttons = [...lifestyle.querySelectorAll('[data-ts-lifestyle-state]')];
    const label = lifestyle.querySelector('[data-ts-lifestyle-label]');
    let userControlled = false;
    let autoTimer = 0;

    const setLifestyleState = (state, userInitiated = false) => {
      if (!visual || !['closed', 'open'].includes(state)) return;
      visual.dataset.state = state;
      buttons.forEach((button) => {
        const active = button.dataset.tsLifestyleState === state;
        button.setAttribute('aria-pressed', String(active));
      });
      if (label) label.textContent = state === 'open' ? 'KEY ON · DRS OPEN' : 'WING CLOSED';
      if (userInitiated) {
        userControlled = true;
        window.clearTimeout(autoTimer);
      }
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => setLifestyleState(button.dataset.tsLifestyleState, true));
    });

    if (!reduceMotion && 'IntersectionObserver' in window) {
      const lifestyleObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || userControlled) return;
          lifestyleObserver.unobserve(entry.target);
          autoTimer = window.setTimeout(() => setLifestyleState('open'), compactMobile ? 560 : 850);
        });
      }, { threshold: compactMobile ? .28 : .45 });
      lifestyleObserver.observe(lifestyle);
    }
  }

  if (reduceMotion) return;

  document.documentElement.classList.add('ts-motion-ready');
  const stage = document.querySelector('[data-ts-stage]');
  const desktopStage = window.matchMedia('(min-width: 990px)');
  if (stage) {
    const stageImage = stage.querySelector('.ts-stage__product img');
    let stageActivated = false;
    const activateStage = () => {
      if (stageActivated) return;
      stageActivated = true;
      requestAnimationFrame(() => requestAnimationFrame(() => stage.classList.add('ts-stage-ready')));
    };
    const activationFallback = window.setTimeout(activateStage, 1200);
    if (stageImage?.decode) {
      stageImage.decode().catch(() => {}).then(() => {
        window.clearTimeout(activationFallback);
        activateStage();
      });
    } else if (stageImage && !stageImage.complete) {
      stageImage.addEventListener('load', () => {
        window.clearTimeout(activationFallback);
        activateStage();
      }, { once: true });
    } else {
      window.clearTimeout(activationFallback);
      activateStage();
    }
  }
  if (stage) {
    let stageFrame = 0;
    let stageRange = Math.max(stage.offsetHeight - window.innerHeight, 1);
    const stageMotionProperties = [
      '--ts-stage-progress', '--ts-stage-product-scale', '--ts-stage-product-rotate',
      '--ts-stage-product-x', '--ts-stage-product-y', '--ts-stage-word-x',
      '--ts-stage-word-opacity', '--ts-stage-copy-opacity', '--ts-stage-copy-y',
      '--ts-stage-telemetry-opacity', '--ts-stage-telemetry-y', '--ts-stage-carbon-shift',
      '--ts-stage-pointer-x', '--ts-stage-pointer-y', '--ts-stage-pointer-rotate'
    ];
    const clearStageMotion = () => stageMotionProperties.forEach((property) => stage.style.removeProperty(property));
    const smoothstep = (edge0, edge1, value) => {
      const unit = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
      return unit * unit * (3 - 2 * unit);
    };
    const updateStage = () => {
      if (!desktopStage.matches) {
        clearStageMotion();
        stageFrame = 0;
        return;
      }
      const rect = stage.getBoundingClientRect();
      const rawProgress = Math.max(0, -rect.top / stageRange);
      // Complete the choreography before the sticky scene releases so the
      // final composition remains visible instead of ending on an empty frame.
      const progress = Math.max(0, Math.min(1, rawProgress / .68));
      const productLift = Math.sin(progress * Math.PI);
      const copyIn = smoothstep(.16, .38, progress);
      const copyOpacity = copyIn;
      const wordOpacity = 1 - smoothstep(.28, .56, progress);
      const telemetryOpacity = 1 - smoothstep(.08, .42, progress) * .78;
      stage.style.setProperty('--ts-stage-progress', String(progress));
      stage.style.setProperty('--ts-stage-product-scale', String(.88 + productLift * .22 - progress * .045));
      stage.style.setProperty('--ts-stage-product-rotate', `${-1.5 + progress * 15}deg`);
      stage.style.setProperty('--ts-stage-product-x', `${progress * 9.5}vw`);
      stage.style.setProperty('--ts-stage-product-y', `${-productLift * 4.2}vh`);
      stage.style.setProperty('--ts-stage-word-x', `${progress * -5.5}vw`);
      stage.style.setProperty('--ts-stage-word-opacity', String(wordOpacity));
      stage.style.setProperty('--ts-stage-copy-opacity', String(copyOpacity));
      stage.style.setProperty('--ts-stage-copy-y', `${(1 - copyIn) * 30}px`);
      stage.style.setProperty('--ts-stage-telemetry-opacity', String(telemetryOpacity));
      stage.style.setProperty('--ts-stage-telemetry-y', `${progress * 18}px`);
      stage.style.setProperty('--ts-stage-carbon-shift', `${progress * 5.5}vh`);
      stageFrame = 0;
    };
    const requestStageUpdate = () => {
      if (!stageFrame) stageFrame = requestAnimationFrame(updateStage);
    };
    window.addEventListener('scroll', requestStageUpdate, { passive: true });
    window.addEventListener('resize', () => {
      stageRange = Math.max(stage.offsetHeight - window.innerHeight, 1);
      requestStageUpdate();
    }, { passive: true });
    desktopStage.addEventListener('change', () => {
      stageRange = Math.max(stage.offsetHeight - window.innerHeight, 1);
      requestStageUpdate();
    });
    stage.addEventListener('pointermove', (event) => {
      if (!desktopStage.matches) return;
      const rect = stage.getBoundingClientRect();
      const pointerX = ((event.clientX - rect.left) / rect.width - .5) * 2;
      const pointerY = ((event.clientY - Math.max(rect.top, 0)) / Math.min(rect.height, window.innerHeight) - .5) * 2;
      stage.style.setProperty('--ts-stage-pointer-x', `${pointerX * 16}px`);
      stage.style.setProperty('--ts-stage-pointer-y', `${pointerY * 10}px`);
      stage.style.setProperty('--ts-stage-pointer-rotate', `${pointerX * .8}deg`);
    }, { passive: true });
    stage.addEventListener('pointerleave', () => {
      if (!desktopStage.matches) return;
      stage.style.setProperty('--ts-stage-pointer-x', '0px');
      stage.style.setProperty('--ts-stage-pointer-y', '0px');
      stage.style.setProperty('--ts-stage-pointer-rotate', '0deg');
    }, { passive: true });
    updateStage();
  }
  const hero = document.querySelector('.ts-hero');
  requestAnimationFrame(() => requestAnimationFrame(() => hero?.classList.add('ts-hero-ready')));
  if (!compactMobile) {
    let heroFrame = 0;
    const updateHeroExit = () => {
      if (!hero) return;
      const start = 70;
      const distance = Math.max(hero.offsetHeight * 0.72, 420);
      const progress = Math.max(0, Math.min(1, (window.scrollY - start) / distance));
      const eased = progress * progress * (3 - 2 * progress);
      hero.style.setProperty('--ts-hero-exit-opacity', String(1 - eased * 0.94));
      hero.style.setProperty('--ts-hero-exit-y', `${eased * -92}px`);
      hero.style.setProperty('--ts-hero-exit-scale', String(1 - eased * 0.035));
      heroFrame = 0;
    };
    const onHeroScroll = () => {
      if (!heroFrame) heroFrame = requestAnimationFrame(updateHeroExit);
    };
    updateHeroExit();
    window.addEventListener('scroll', onHeroScroll, { passive: true });
  }
  const revealItems = document.querySelectorAll('.ts-home section:not(.ts-hero):not(.ts-stage):not(.ts-features), .ts-track, .footer');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('ts-is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10%', threshold: 0.08 });
  revealItems.forEach((item, index) => {
    item.classList.add('ts-reveal');
    item.style.setProperty('--ts-reveal-delay', `${Math.min(index * 45, 180)}ms`);
    observer.observe(item);
  });

  // The header group contains separate announcement and navigation sections.
  // Animate only the navigation; animating the first group section selects the
  // announcement bar and makes both sticky sections overlap on scroll reveal.
  const header = document.querySelector('.section-header.shopify-section-group-header-group');
  if (header && !compactMobile) {
    // Dawn also attaches an on-scroll-up controller to this same section. Two
    // controllers can briefly apply opposing top/transform states and expose
    // the section's light canvas as a white flash. Desktop uses this single
    // motion controller; Dawn remains untouched on compact mobile layouts.
    const dawnStickyHeader = header.querySelector('sticky-header');
    if (dawnStickyHeader?.onScrollHandler) {
      window.removeEventListener('scroll', dawnStickyHeader.onScrollHandler);
      header.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'scrolled-past-header',
        'animate'
      );
    }
    let previousY = window.scrollY;
    let directionalTravel = 0;
    let previousDirection = 0;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - previousY;
        const direction = Math.sign(delta);
        if (direction && direction !== previousDirection) directionalTravel = 0;
        directionalTravel += delta;
        if (direction) previousDirection = direction;
        header.classList.toggle('ts-header-dark', currentY > 72);
        if (currentY > 220 && directionalTravel > 72) {
          header.classList.add('ts-header-hidden');
          directionalTravel = 0;
        } else if (directionalTravel < -38 || currentY < 96) {
          header.classList.remove('ts-header-hidden');
          directionalTravel = 0;
        }
        previousY = currentY;
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }
});
